import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'

const parsePixels = (value: string) => {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getScrollableAncestors = (element: HTMLElement) => {
  const ancestors: HTMLElement[] = []
  let current = element.parentElement

  while (
    current &&
    current !== document.body &&
    current !== document.documentElement
  ) {
    if (
      /auto|scroll|overlay/.test(window.getComputedStyle(current).overflowY)
    ) {
      ancestors.push(current)
    }
    current = current.parentElement
  }

  return ancestors
}

const getViewportBottom = () => {
  const viewport = window.visualViewport
  return viewport ? viewport.offsetTop + viewport.height : window.innerHeight
}

const getAvailableHeight = (
  textarea: HTMLTextAreaElement,
  boundary: HTMLElement,
  viewportOffset: number
) => {
  const textareaRect = textarea.getBoundingClientRect()
  const boundaryRect = boundary.getBoundingClientRect()
  const spaceBelow = Math.max(boundaryRect.bottom - textareaRect.bottom, 0)
  const scrollContainer = getScrollableAncestors(textarea)[0]

  if (!scrollContainer) {
    return getViewportBottom() - textareaRect.top - spaceBelow - viewportOffset
  }

  const containerRect = scrollContainer.getBoundingClientRect()
  const textareaContentTop =
    textareaRect.top - containerRect.top + scrollContainer.scrollTop
  const containerViewportBottom =
    Math.min(containerRect.bottom, getViewportBottom()) - containerRect.top

  return (
    containerViewportBottom - textareaContentTop - spaceBelow - viewportOffset
  )
}

interface InlineStylesI {
  height: string
  overflowY: string
}

interface UseAutoResizeTextareaIProps {
  boundaryRef?: RefObject<HTMLElement>
  enabled: boolean
  /** True once a manual drag owns the height, so nothing is restored over it. */
  hasManualHeight?: boolean
  maxRows?: number
  /** Owned by the caller so drag-to-resize can share the same element. */
  textareaRef: RefObject<HTMLTextAreaElement>
  value: string
  viewportOffset?: number
}

export const useAutoResizeTextarea = ({
  boundaryRef,
  enabled,
  hasManualHeight = false,
  maxRows,
  textareaRef,
  value,
  viewportOffset = 0,
}: UseAutoResizeTextareaIProps) => {
  const initialStylesRef = useRef<InlineStylesI | null>(null)
  const managedStylesRef = useRef<InlineStylesI | null>(null)
  const wasEnabledRef = useRef(false)

  const restoreManagedStyles = useCallback(() => {
    const textarea = textareaRef.current
    const initialStyles = initialStylesRef.current
    const managedStyles = managedStylesRef.current
    if (!textarea || !initialStyles || !managedStyles) return

    if (textarea.style.height === managedStyles.height) {
      textarea.style.height = initialStyles.height
    }
    if (textarea.style.overflowY === managedStyles.overflowY) {
      textarea.style.overflowY = initialStyles.overflowY
    }

    initialStylesRef.current = null
    managedStylesRef.current = null
  }, [textareaRef])

  const resizeToContent = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea || !enabled || (boundaryRef && !boundaryRef.current)) return

    if (!initialStylesRef.current) {
      initialStylesRef.current = {
        height: textarea.style.height,
        overflowY: textarea.style.overflowY,
      }
    }

    const scrollPositions = getScrollableAncestors(textarea).map(
      (element) => [element, element.scrollTop] as const
    )
    const scrollingElement = document.scrollingElement
    const pageScrollTop = scrollingElement?.scrollTop
    const textareaScrollTop = textarea.scrollTop

    textarea.style.height = 'auto'
    textarea.style.overflowY = 'hidden'

    const styles = window.getComputedStyle(textarea)
    const minHeight = parsePixels(styles.minHeight)
    const lineHeight = Number.parseFloat(styles.lineHeight)
    const verticalPadding =
      parsePixels(styles.paddingTop) + parsePixels(styles.paddingBottom)
    const verticalBorder =
      parsePixels(styles.borderTopWidth) + parsePixels(styles.borderBottomWidth)
    const rowsHeight =
      maxRows && maxRows > 0 && Number.isFinite(lineHeight)
        ? lineHeight * maxRows + verticalPadding + verticalBorder
        : Number.POSITIVE_INFINITY

    const availableHeight = boundaryRef?.current
      ? getAvailableHeight(textarea, boundaryRef.current, viewportOffset)
      : Number.POSITIVE_INFINITY
    const contentHeight = textarea.scrollHeight
    const height = Math.max(
      minHeight,
      Math.min(contentHeight, availableHeight, rowsHeight)
    )

    textarea.style.height = `${height}px`
    textarea.style.overflowY = contentHeight > height ? 'auto' : 'hidden'
    managedStylesRef.current = {
      height: textarea.style.height,
      overflowY: textarea.style.overflowY,
    }

    scrollPositions.forEach(([element, scrollTop]) => {
      element.scrollTop = scrollTop
    })
    if (scrollingElement && pageScrollTop !== undefined) {
      scrollingElement.scrollTop = pageScrollTop
    }
    textarea.scrollTop = textareaScrollTop
  }, [boundaryRef, enabled, maxRows, textareaRef, viewportOffset])

  useLayoutEffect(() => {
    const shouldRestoreStyles = !enabled && wasEnabledRef.current
    wasEnabledRef.current = enabled

    if (!enabled) {
      // A manual drag is a handoff, not a teardown: restoring here would strip
      // the dragged height whenever it happens to equal the last auto height.
      if (shouldRestoreStyles && !hasManualHeight) restoreManagedStyles()
      return
    }

    resizeToContent()
  }, [enabled, hasManualHeight, resizeToContent, restoreManagedStyles, value])

  useEffect(() => {
    if (boundaryRef?.current) resizeToContent()
  }, [boundaryRef, resizeToContent])

  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!enabled || !textarea) return

    let previousWidth = textarea.getBoundingClientRect().width
    let observedBoundary = boundaryRef?.current
    let previousBoundary = observedBoundary
    let previousSpaceBelow = observedBoundary
      ? observedBoundary.getBoundingClientRect().bottom -
        textarea.getBoundingClientRect().bottom
      : 0
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver((_entries, currentObserver) => {
            const nextBoundary = boundaryRef?.current
            if (nextBoundary !== observedBoundary) {
              if (observedBoundary) {
                currentObserver.unobserve(observedBoundary)
              }
              if (nextBoundary) currentObserver.observe(nextBoundary)
              observedBoundary = nextBoundary
            }

            const nextWidth = textarea.getBoundingClientRect().width
            const nextSpaceBelow = nextBoundary
              ? nextBoundary.getBoundingClientRect().bottom -
                textarea.getBoundingClientRect().bottom
              : 0
            const didBoundaryChange = nextBoundary !== previousBoundary
            if (
              !didBoundaryChange &&
              nextWidth === previousWidth &&
              nextSpaceBelow === previousSpaceBelow
            ) {
              return
            }

            previousWidth = nextWidth
            previousBoundary = nextBoundary
            previousSpaceBelow = nextSpaceBelow
            resizeToContent()
          })

    observer?.observe(textarea)
    if (observedBoundary) observer?.observe(observedBoundary)
    window.addEventListener('resize', resizeToContent)
    window.visualViewport?.addEventListener('resize', resizeToContent)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', resizeToContent)
      window.visualViewport?.removeEventListener('resize', resizeToContent)
    }
  }, [boundaryRef, enabled, resizeToContent, textareaRef])
}
